/* eslint-disable react/jsx-props-no-spreading,react/no-unknown-property */
import { Text, useGLTF } from '@react-three/drei'
import type { ThreeEvent } from '@react-three/fiber'
import React, { Fragment, useEffect, useMemo } from 'react'
import * as THREE from 'three'

import SceneContext from './context'
import HoverHitBox from './objects/hover-hitbox'
import type {
  ControlOptions,
  INTERACTION_HOVER,
  INTERACTION_POINT,
  ModelControlsInterface,
} from './objects/hover-hitbox' 

import {
  type ParallaxMaterialProps,
} from './objects/parallax-material'

import PhysicsHitBox from './objects/physics-hitbox'

import type { GLTFData } from './types'
import { PointerType, usePointerType } from './util/use-pointer-type'

export interface ParalaxAssetProps {
  [keys: string]: ParallaxMaterialProps
}

type GLTFNodes = {
  [key: string]: THREE.Mesh
}

type GLTFMaterials = {
  [key: string]: THREE.Material
}

type GLTFResult = {
  nodes: GLTFNodes
  materials: GLTFMaterials
}

export interface GLTFBuilderProps {
  gltfSrc: string
  visible?: boolean
  modelControl?: ModelControlsInterface
  getHighlightedTarget?: (arg: string) => void
  getNodes?: (nodes: GLTFNodes) => void
  getClickTargetPosition?: (position: Array<number>) => void
  modelLoaded?: (arg: boolean) => void
  interactionMode?: INTERACTION_HOVER | INTERACTION_POINT
  disableCameraLoadingCntrl?: boolean
  isMoving: boolean
  requireActionForHover: boolean
  onGLTFDataFound?: (data: GLTFData) => void
  firstPersonMode?: boolean
  parallaxAssets?: ParalaxAssetProps
}

function GLTFBuilder({
  gltfSrc,
  modelControl,
  visible = true,
  getHighlightedTarget,
  getNodes,
  getClickTargetPosition,
  modelLoaded,
  interactionMode = 'hover',
  disableCameraLoadingCntrl = false,
  isMoving,
  requireActionForHover,
  onGLTFDataFound,
  firstPersonMode = false,
  parallaxAssets,
}: GLTFBuilderProps) {
  const { hitBoxGroupName, hitboxVisible } = React.useContext(SceneContext)

  const gltfData = useGLTF(gltfSrc)

  useEffect(() => {
    if (gltfSrc) {
      useGLTF.preload(gltfSrc)
    }
  }, [gltfSrc])

  const { nodes, materials } = gltfData as unknown as GLTFResult
  const pointerType = usePointerType()

  const GROUP = 'group'
  const MESH = 'mesh'
  const SCENE = 'scene'

  const validateAction = React.useCallback(
    (controlOptions?: ControlOptions) =>
      !requireActionForHover ||
      !!(controlOptions?.action && controlOptions?.actionTarget),
    []
  )

  const handleOnDoubleClick = React.useCallback(
    (e: ThreeEvent<MouseEvent>) => {
      e.stopPropagation()
      const obj = e.object as THREE.Mesh
      const cntrl = modelControl ? modelControl[obj.name] : undefined
      if (cntrl?.onClick && cntrl.id) {
        cntrl.onClick(cntrl)
      }
    },
    [modelControl]
  )

  const handleOnPointerOver = React.useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation()
      const obj = e.object as THREE.Mesh
      const objMaterial = obj.material as THREE.MeshStandardMaterial
      if (modelControl) {
        const hover = modelControl[obj.name]?.hoverColor
        const canAction = validateAction(modelControl[obj.name])
        if (!requireActionForHover || (hover && canAction)) {
          if (getHighlightedTarget) {
            getHighlightedTarget(modelControl[obj.name]?.id || '')
          }
          if (!hover) {
            return
          }
          obj.visible = true
          objMaterial.color.set(hover ?? new THREE.Color(255, 255, 255))
          objMaterial.transparent = true
          objMaterial.opacity = 0.7
          objMaterial.needsUpdate = true
        }
      }
    },
    [modelControl, validateAction]
  )

  const handleOnPointerOut = React.useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation()
      const obj = e.object as THREE.Mesh
      if (modelControl) {
        if (
          modelControl[obj.name]?.hoverColor &&
          !modelControl[obj.name]?.hoverActive
        ) {
          obj.visible = false
        }
        if (getHighlightedTarget) {
          getHighlightedTarget('')
        }
      }
    },
    [modelControl]
  )

  const isTouchOrPen = useMemo(
    () => pointerType === PointerType.Touch || pointerType === PointerType.Pen,
    [pointerType]
  )

  useEffect(() => {
    if (!isTouchOrPen && isMoving && getHighlightedTarget) {
      getHighlightedTarget('')
    }
  }, [isMoving])

  useEffect(() => {
    onGLTFDataFound?.(gltfData)
  }, [gltfData, onGLTFDataFound])

  const createMesh = (nodeMesh: THREE.Mesh, isInGroup = false) => {
    if (isInGroup) {
      return (
        <mesh
          key={`child-group-mesh-${nodeMesh.name}`}
          name={`child-group-mesh-${nodeMesh.name}`}
          geometry={nodeMesh.geometry}
          position={nodeMesh.position}
          rotation={nodeMesh.rotation}
          scale={nodeMesh.scale}
          material={nodeMesh.material}
        />
      )
    }

    return (
      <mesh
        key={`no-group-mesh-${nodeMesh.name}`}
        name={`no-group-mesh-${nodeMesh.name}`}
        geometry={nodeMesh.geometry}
        position={nodeMesh.position}
        rotation={nodeMesh.rotation}
        scale={nodeMesh.scale}
        onDoubleClick={(e) => handleOnDoubleClick(e)}
        material={nodeMesh.material}
      />
    )
  }

  const createHitBox = (nodeMesh: THREE.Mesh) => {
    if (firstPersonMode) {
      return (
        <PhysicsHitBox
          key={`physics-hitbox-${nodeMesh.name}`}
          node={nodeMesh}
          visible={hitboxVisible}
          getClickTargetPosition={getClickTargetPosition}
        />
      )
    }
    return null
  }

  const connectedOrNotMoving = useMemo(
    () =>
      isTouchOrPen ||
      (!disableCameraLoadingCntrl && !isMoving) ||
      disableCameraLoadingCntrl,
    [isMoving, disableCameraLoadingCntrl, isTouchOrPen]
  )

  const createGroupChildren = (nodeMeshs: THREE.Mesh[]) =>
    nodeMeshs.map((nodeMesh) => {
      if (nodeMesh.geometry?.attributes?.position?.count >= 0) {
        return createMesh(nodeMesh, true)
      }
      return null
    })

  const setHoverHitbox = React.useCallback(
    (currentModelControl: ControlOptions, nodeGroup: THREE.Mesh) => {
      const hasHoverControls =
        currentModelControl?.action && currentModelControl?.actionTarget

      if (firstPersonMode) {
        return null
      }

      if (!requireActionForHover || hasHoverControls) {
        if (currentModelControl?.hoverActive || connectedOrNotMoving) {
          return (
            <HoverHitBox
              interactionMode={interactionMode}
              key={`hover-hitbox-${nodeGroup.name}`}
              handleOnDoubleClick={handleOnDoubleClick}
              handleOnPointerOver={
                connectedOrNotMoving &&
                (!requireActionForHover || currentModelControl?.hoverColor)
                  ? handleOnPointerOver
                  : () => {}
              }
              handleOnPointerOut={
                !requireActionForHover || currentModelControl?.hoverColor
                  ? handleOnPointerOut
                  : () => {}
              }
              isVisible={currentModelControl?.hoverActive}
              materialColor={currentModelControl?.hoverColor}
              node={nodeGroup}
            />
          )
        }
      }
      return null
    },
    [modelControl, requireActionForHover, firstPersonMode]
  )

  const createGroup = (nodeGroup: THREE.Mesh) => {
    const currentModelControl = modelControl
      ? modelControl[nodeGroup.children[0].name]
      : undefined
    return (
      <Fragment key={`group-fragment-${nodeGroup.name}`}>
        {currentModelControl && setHoverHitbox(currentModelControl, nodeGroup)}
        <group
          key={`parent-group-${nodeGroup.name}`}
          name={`parent-group-${nodeGroup.name}`}
          rotation={nodeGroup.rotation}
          position={nodeGroup.position}
          scale={nodeGroup?.scale || 1}
        >
          {nodeGroup.children &&
            createGroupChildren(nodeGroup.children as THREE.Mesh[])}
        </group>
      </Fragment>
    )
  }

  const getModelObjectFromNodes = (fromNodes: GLTFNodes, key: string) => {
    if (fromNodes[key].name.toLocaleLowerCase() === SCENE) {
      return null
    }

    if (fromNodes[key].type.toLowerCase() === GROUP) {
      return createGroup(fromNodes[key])
    }

    if (
      fromNodes[key].type.toLowerCase() === MESH &&
      fromNodes[key].parent?.name.toLocaleLowerCase() ===
        hitBoxGroupName.toLocaleLowerCase()
    ) {
      return createHitBox(fromNodes[key])
    }

    if (
      fromNodes[key].type.toLowerCase() === MESH &&
      fromNodes[key].parent?.name.toLocaleLowerCase() === SCENE
    ) {
      return createMesh(fromNodes[key], false)
    }

    return null
  }

  const buildModel = React.useMemo(() => {
    if (nodes && materials) {
      return Object.keys(nodes)
        .map((key) => getModelObjectFromNodes(nodes, key))
        .filter((node) => node)
    }

    return (
      <Text fontSize={3} color="black" anchorX="center" anchorY="middle">
        Incorrect GLTF data
      </Text>
    )
  }, [
    nodes,
    materials,
    modelControl,
    connectedOrNotMoving,
    hitboxVisible,
    parallaxAssets,
  ])

  React.useEffect(() => {
    if (nodes) {
      getNodes?.(nodes)
    }
  }, [nodes])

  React.useEffect(() => {
    if (buildModel && modelLoaded) {
      modelLoaded(true)
    }
  }, [buildModel])

  return (
    <group visible={visible} dispose={null}>
      {buildModel}
    </group>
  )
}

export default GLTFBuilder
